import { useCallback, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { HaiIcon } from '@/components/icons';
import {
  isMeaningfulRect,
  rectFromDrag,
  type Annotation,
  type AnnotationRect,
} from '@/lib/diagnostics/annotationModel';
import type { CapturedShot } from '@/lib/diagnostics/screenshotCapture';

/**
 * ScreenshotAnnotator — 在截圖上圈出問題(R250)
 * =============================================================================
 *
 * 拖曳畫框。R257 起只存幾何 —— 元件反查隨自動截圖一起移除(理由見
 * [screenshotCapture](../lib/diagnostics/screenshotCapture.ts) 檔頭)。
 *
 * ## 為什麼編輯用 SVG、輸出用 canvas
 *
 * 編輯期的框是 React state → 用 `<svg><rect>` 宣告式畫最省事(新增 / 刪除 / hover 都不必
 * 手動重繪,刪除鈕也能直接掛在 SVG 元素上)。輸出時才用 canvas 把框**燒進圖片** ——
 * 這樣後端與 AI 打開圖片就直接看到紅框,不必自己拿座標去疊。
 * 兩套繪製共用同一份 `rect`,視覺一致就夠,不需要 pixel-perfect。
 *
 */

export interface ScreenshotAnnotatorProps {
  shot: CapturedShot;
  annotations: readonly Annotation[];
  onChange: (next: readonly Annotation[]) => void;
}

export function ScreenshotAnnotator({ shot, annotations, onChange }: ScreenshotAnnotatorProps) {
  const { t } = useTranslation('common');
  const boxRef = useRef<HTMLDivElement>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragNow, setDragNow] = useState<{ x: number; y: number } | null>(null);

  /** 滑鼠事件座標 → 截圖像素座標(元素被 CSS 縮放過,必須用實際顯示尺寸換算)。 */
  const toShot = useCallback(
    (e: ReactPointerEvent): { x: number; y: number } => {
      const box = boxRef.current;
      if (!box) return { x: 0, y: 0 };
      const r = box.getBoundingClientRect();
      const scale = r.width > 0 ? shot.width / r.width : 1;
      return { x: (e.clientX - r.left) * scale, y: (e.clientY - r.top) * scale };
    },
    [shot.width],
  );

  function handleDown(e: ReactPointerEvent): void {
    // 只接主鍵拖曳;右鍵留給瀏覽器選單(「另存圖片」在這裡是合理的期待)。
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = toShot(e);
    setDragStart(p);
    setDragNow(p);
  }

  function handleMove(e: ReactPointerEvent): void {
    if (!dragStart) return;
    setDragNow(toShot(e));
  }

  function handleUp(): void {
    if (!dragStart || !dragNow) {
      setDragStart(null);
      setDragNow(null);
      return;
    }
    const rect = rectFromDrag(dragStart, dragNow);
    setDragStart(null);
    setDragNow(null);
    // 太小的框當誤觸丟掉 —— 使用者只是點了一下圖片。
    if (!isMeaningfulRect(rect)) return;

    onChange([...annotations, { rect }]);
  }

  function removeAt(index: number): void {
    onChange(annotations.filter((_, i) => i !== index));
  }

  const preview: AnnotationRect | null =
    dragStart && dragNow ? rectFromDrag(dragStart, dragNow) : null;

  return (
    <div>
      <div
        ref={boxRef}
        style={frameStyle}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        data-testid="shot-annotator"
      >
        <img src={shot.dataUrl} alt="" style={imgStyle} draggable={false} />
        <svg
          viewBox={`0 0 ${shot.width} ${shot.height}`}
          style={svgStyle}
          preserveAspectRatio="none"
          aria-hidden
        >
          {annotations.map((a, i) => (
            <g key={`${a.rect.join('-')}-${i}`}>
              <rect
                x={a.rect[0]}
                y={a.rect[1]}
                width={a.rect[2]}
                height={a.rect[3]}
                fill="none"
                // 圈出「有問題的地方」= CIS 錯誤語意 → rose(R266:--err 只當淡底)
                stroke={ANNOTATION_STROKE}
                strokeWidth={Math.max(2, shot.width / 400)}
              />
            </g>
          ))}
          {preview && (
            <rect
              x={preview[0]}
              y={preview[1]}
              width={preview[2]}
              height={preview[3]}
              fill="none"
              stroke={ANNOTATION_STROKE}
              strokeWidth={Math.max(2, shot.width / 400)}
              strokeDasharray={`${Math.max(4, shot.width / 160)}`}
            />
          )}
        </svg>
      </div>
      <p style={hintStyle}>{t('report.shot.annotateHint')}</p>
      {annotations.length > 0 && (
        <ul style={listStyle} data-testid="shot-annotation-list">
          {annotations.map((a, i) => (
            <li key={`${a.rect.join('-')}-${i}`} style={itemStyle}>
              <span style={itemTextStyle}>
                {/* 只有幾何(R257 移除元件反查)。顯示座標讓使用者看得出這一筆對應哪個框,
                    刪錯的時候有依據。 */}
                {`${a.rect[0]}, ${a.rect[1]} · ${a.rect[2]}×${a.rect[3]}`}
              </span>
              <button
                type="button"
                onClick={() => removeAt(i)}
                style={removeBtnStyle}
                aria-label={t('report.shot.removeAnnotation')}
                title={t('report.shot.removeAnnotation')}
                data-testid={`shot-annotation-remove-${i}`}
              >
                <HaiIcon name="x" size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const frameStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  borderRadius: 'var(--r-md)',
  overflow: 'hidden',
  border: '1px solid var(--hairline-strong)',
  // 拖曳畫框時不要讓瀏覽器把手勢當成捲動 / 選字。
  touchAction: 'none',
  userSelect: 'none',
  cursor: 'crosshair',
  lineHeight: 0,
};
const imgStyle: CSSProperties = { display: 'block', width: '100%', height: 'auto' };
const svgStyle: CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%' };
/** 標註框線色:CIS 錯誤 / 問題 = rose(--rose-600 兩主題同值,壓在任意截圖上都醒目)。 */
const ANNOTATION_STROKE = 'var(--rose-600)';
const hintStyle: CSSProperties = {
  margin: '7px 0 0',
  fontSize: 12,
  color: 'var(--text-secondary)',
  lineHeight: 1.6,
};
const listStyle: CSSProperties = {
  listStyle: 'none',
  margin: '8px 0 0',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
};
const itemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '5px 9px',
  borderRadius: 8,
  background: 'var(--ink-950)',
  fontSize: 11.5,
};
const itemTextStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  color: 'var(--text-primary)',
};
const removeBtnStyle: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: '0 2px',
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
};
